import React from 'react';
import ReactMarkdown, { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  Typography,
  Link,
  Box,
  Divider,
  useTheme,
  List,
  ListItem,
} from '@mui/material';

interface MarkdownRendererProps {
  content: string;
}

export function MarkdownRenderer({ content }: MarkdownRendererProps) {
  const theme = useTheme();

  const components: Partial<Components> = {
    h1: ({ children }: { children?: React.ReactNode }) => (
      <Typography variant="headingSm" gutterBottom sx={{ mt: 2 }}>
        {children}
      </Typography>
    ),
    h2: ({ children }: { children?: React.ReactNode }) => (
      <Typography variant="h4" gutterBottom sx={{ mt: 2 }}>
        {children}
      </Typography>
    ),
    h3: ({ children }: { children?: React.ReactNode }) => (
      <Typography variant="h5" gutterBottom sx={{ fontWeight: 600, mt: 1.5 }}>
        {children}
      </Typography>
    ),

    p: ({ children }: { children?: React.ReactNode }) => (
      <Typography
        variant="bodyMd"
        paragraph
        sx={{
          color: theme.palette.grey[800],
          mb: 2.5,
        }}
      >
        {children}
      </Typography>
    ),

    strong: ({ children }: { children?: React.ReactNode }) => (
      <Box
        component="span"
        sx={{
          ...theme.typography.bodyMd,
          // fontWeight must come after the bodyMd spread: bodyMd sets
          // fontWeight 400, which would otherwise override the bold and
          // un-bold the <strong> text.
          fontWeight: 700,
          color: theme.palette.grey[800],
        }}
      >
        {children}
      </Box>
    ),

    em: ({ children }: { children?: React.ReactNode }) => (
      <Box component="span" sx={{ fontStyle: 'italic' }}>
        {children}
      </Box>
    ),

    a: ({ href, children }: { href?: string; children?: React.ReactNode }) => (
      <Link
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        sx={{
          color: theme.palette.brand.primary,
          textDecoration: 'underline',
          '&:hover': {
            color: theme.palette.brand.accent,
          },
        }}
      >
        {children}
      </Link>
    ),

    ul: ({ children }: { children?: React.ReactNode }) => (
      <List
        sx={{
          listStyleType: 'disc',
          pl: 2,
          py: 0,
          mb: 2.5,
          '& .MuiListItem-root': {
            display: 'list-item',
            p: 0,
            pl: 1,
          },
        }}
      >
        {children}
      </List>
    ),
    ol: ({ children }: { children?: React.ReactNode }) => (
      <List
        sx={{
          listStyleType: 'decimal',
          pl: 2,
          py: 0,
          mb: 2.5,
          '& .MuiListItem-root': {
            display: 'list-item',
            p: 0,
            pl: 1,
          },
        }}
      >
        {children}
      </List>
    ),
    li: ({ children }: { children?: React.ReactNode }) => (
      <ListItem sx={{ display: 'list-item', py: 0.25 }}>
        <Typography
          variant="bodyMd"
          sx={{
            color: theme.palette.grey[800],
          }}
        >
          {children}
        </Typography>
      </ListItem>
    ),

    code: ({ inline, children }: { inline?: boolean; children?: React.ReactNode }) => {
      if (inline) {
        return (
          <Box
            component="code"
            sx={{
              backgroundColor: theme.palette.grey[100],
              color: theme.palette.error.dark,
              padding: `${theme.spacing(0.25)} ${theme.spacing(0.75)}`,
              borderRadius: theme.spacing(0.5),
              fontFamily: 'monospace',
              fontSize: '0.9em',
            }}
          >
            {children}
          </Box>
        );
      }
      return (
        <Box
          component="pre"
          sx={{
            backgroundColor: theme.palette.grey[100],
            padding: theme.spacing(2),
            borderRadius: theme.spacing(1),
            overflow: 'auto',
            my: 1.5,
          }}
        >
          <code style={{ fontFamily: 'monospace', fontSize: '0.9em' }}>
            {children}
          </code>
        </Box>
      );
    },

    blockquote: ({ children }: { children?: React.ReactNode }) => (
      <Box
        sx={{
          borderLeft: `${theme.spacing(0.5)} solid ${theme.palette.brand.primary}`,
          pl: 2,
          py: 0.5,
          my: 1.5,
          color: theme.palette.grey[600],
          fontStyle: 'italic',
        }}
      >
        {children}
      </Box>
    ),

    hr: () => (
      <Divider
        sx={{
          my: 2.5,
          borderColor: theme.palette.grey[300],
          opacity: 0.6,
        }}
      />
    ),
  };

  return (
    <Box sx={{
      '& > *:first-of-type': { mt: 0 },
      '& > *:last-child': { mb: 0 },
    }}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={components}
      >
        {content}
      </ReactMarkdown>
    </Box>
  );
}
